/* eslint-disable react-hooks/exhaustive-deps */
'use client';

import { sendGTMEvent } from '@next/third-parties/google';
import Autoplay from 'embla-carousel-autoplay';
import useEmblaCarousel from 'embla-carousel-react';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import { motion } from 'motion/react';
import dynamic from 'next/dynamic';
import { usePathname } from 'next/navigation';
import {
  ComponentProps,
  HTMLAttributes,
  KeyboardEvent,
  forwardRef,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { useWindowSize } from 'usehooks-ts';

import { cn } from '@natu/utils/cn';
import { PredefinedGAEvents } from '@natu/utils/events';
import { getEventPayload, type EventProps } from '@natu/utils/getEventPayload';
import { isArrayWithLength } from '@natu/utils/isArrayWithLength';
import { usePageContext } from '@natu/utils/PageContextProvider';

import { Button } from '../Button/Button';
import { useCustomCursorContext } from '../Decorators/CustomCursor/CustomCursorProvider';
import { CarouselApi, CarouselContext, CarouselProps } from './CarouselContext';

const Skeleton = dynamic(() => import('../Skeleton/Skeleton').then(mod => mod.Skeleton));

function useCarousel() {
  const context = useContext(CarouselContext);

  if (!context) {
    throw new Error('useCarousel must be used within a <Carousel />');
  }

  return context;
}

interface CarouselPropsComponentProps extends React.HTMLAttributes<HTMLDivElement>, CarouselProps {
  autoplay?: boolean;
  autoplayDelay?: number;
  disableCustomCursor?: boolean;
  event?: PredefinedGAEvents;
  eventProps?: EventProps;
}

const Carousel = forwardRef<HTMLDivElement, CarouselPropsComponentProps>(
  (
    {
      opts,
      setApi,
      className,
      plugins,
      children,
      autoplay,
      autoplayDelay = 2000,
      disableCustomCursor = false,
      event,
      eventProps,
      ...props
    },
    ref,
  ) => {
    const carouselPlugins = autoplay ? [Autoplay({ delay: autoplayDelay })] : plugins;

    const { width } = useWindowSize();

    const [carouselRef, api] = useEmblaCarousel(
      {
        ...opts,
        watchDrag: width < 1024,
        axis: 'x',
      },
      carouselPlugins,
    );
    const [canScrollPrev, setCanScrollPrev] = useState(false);
    const [canScrollNext, setCanScrollNext] = useState(false);
    const [selectedIndex, setSelectedIndex] = useState(0);
    const [scrollSnaps, setScrollSnaps] = useState<number[]>([]);
    const [scrollProgress, setScrollProgress] = useState(0);

    const onScroll = useCallback((carouselApi: CarouselApi) => {
      if (!carouselApi) return;

      const progress = carouselApi.scrollProgress();
      setScrollProgress(progress);
    }, []);

    const onSelect = useCallback((carouselApi: CarouselApi) => {
      if (!carouselApi) {
        return;
      }
      setSelectedIndex(carouselApi.selectedScrollSnap());
      setCanScrollPrev(carouselApi.canScrollPrev());
      setCanScrollNext(carouselApi.canScrollNext());
    }, []);

    const onInit = useCallback(
      (carouselApi: CarouselApi) => {
        if (!carouselApi) {
          return;
        }

        setScrollSnaps(carouselApi.scrollSnapList());
        onScroll(carouselApi);
      },
      [onScroll],
    );

    const scrollPrev = useCallback(() => {
      api?.scrollPrev();
    }, [api]);

    const scrollNext = useCallback(() => {
      api?.scrollNext();
    }, [api]);

    const scrollTo = useCallback((index: number) => api?.scrollTo(index), [api]);

    const handleKeyDown = useCallback(
      (event: KeyboardEvent<HTMLDivElement>) => {
        if (event.key === 'ArrowLeft') {
          event.preventDefault();
          scrollPrev();
        } else if (event.key === 'ArrowRight') {
          event.preventDefault();
          scrollNext();
        }
      },
      [scrollPrev, scrollNext],
    );

    const pathname = usePathname();
    const { isCampaign } = usePageContext() || {};
    const eventPayload = event ? getEventPayload({ pathname, event, eventProps, isCampaign }) : {};

    useEffect(() => {
      if (!api || !setApi) {
        return;
      }

      setApi(api);
    }, [api, setApi]);

    useEffect(() => {
      if (!api) {
        return;
      }

      onInit(api);
      onSelect(api);

      api.on('reInit', onSelect);
      api.on('select', onSelect);
      api.on('scroll', () => onScroll(api));

      if (event) {
        api.on('select', () => sendGTMEvent({ event, ...eventPayload }));
      }

      return () => {
        api?.off('select', onSelect);
        api?.off('scroll', onScroll);
      };
    }, [api, onSelect, onInit, onScroll]);

    return (
      <CarouselContext.Provider
        value={{
          carouselRef,
          api,
          opts,
          scrollPrev,
          scrollNext,
          scrollTo,
          canScrollPrev,
          canScrollNext,
          selectedIndex,
          scrollSnaps,
          disableCustomCursor,
          scrollProgress,
        }}
      >
        <div
          ref={ref}
          onKeyDownCapture={handleKeyDown}
          className={cn('relative', className)}
          role="region"
          aria-roledescription="carousel"
          {...props}
        >
          {children}
        </div>
      </CarouselContext.Provider>
    );
  },
);
Carousel.displayName = 'Carousel';

const CarouselContent = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => {
    const {
      carouselRef,
      disableCustomCursor,
      api,
      scrollPrev,
      scrollNext,
      canScrollPrev,
      canScrollNext,
    } = useCarousel();

    const pathname = usePathname();

    const { handleSetCursorState, handleConditionalClassName } = useCustomCursorContext();

    const [hoverSide, setHoverSide] = useState<'left' | 'right' | null>(null);

    const handleMouseMove = useCallback(
      (e: React.MouseEvent<HTMLDivElement>) => {
        if (disableCustomCursor) return;

        const container = e.currentTarget;
        const rect = container.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const isLeftSide = x < rect.width / 2;

        setHoverSide(isLeftSide ? 'left' : 'right');
      },
      [disableCustomCursor],
    );

    const handleClick = useCallback(() => {
      if (!api) return;
      if (hoverSide === 'left' && canScrollPrev) scrollPrev();
      if (hoverSide === 'right' && canScrollNext) scrollNext();
    }, [api, canScrollNext, canScrollPrev, hoverSide, scrollNext, scrollPrev]);

    const arrowClass = 'size-12 text-white';

    useEffect(() => {
      if (disableCustomCursor || hoverSide === null) return;

      const canScroll = hoverSide === 'left' ? canScrollPrev : canScrollNext;

      handleConditionalClassName(canScroll ? null : 'opacity-50');

      let arrowComponent;

      if (hoverSide === 'left') {
        arrowComponent = canScrollPrev ? (
          <ArrowLeft className={arrowClass} />
        ) : (
          <ArrowRight className={arrowClass} />
        );
      } else {
        arrowComponent = canScrollNext ? (
          <ArrowRight className={arrowClass} />
        ) : (
          <ArrowLeft className={arrowClass} />
        );
      }

      handleSetCursorState({
        content: arrowComponent,
        variant: 'click',
      });
    }, [hoverSide, disableCustomCursor, canScrollPrev, canScrollNext]);

    const onMouseLeave = useCallback(() => {
      if (disableCustomCursor) return;

      setHoverSide(null);
      handleSetCursorState({ content: '', variant: 'none' });
    }, [disableCustomCursor]);

    useEffect(() => {
      if (disableCustomCursor) return;

      handleSetCursorState({ content: '', variant: 'none' });
    }, [disableCustomCursor, pathname]);

    return (
      <div
        ref={carouselRef}
        className="overflow-hidden"
        onMouseMove={handleMouseMove}
        onMouseLeave={onMouseLeave}
        onClick={handleClick}
      >
        <div ref={ref} className={cn('-ml-4 flex', className)} {...props} />
      </div>
    );
  },
);
CarouselContent.displayName = 'CarouselContent';

const CarouselItem = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      role="group"
      aria-roledescription="slide"
      className={cn('min-w-0 shrink-0 grow-0 basis-full pl-4', className)}
      {...props}
    />
  ),
);
CarouselItem.displayName = 'CarouselItem';

type PaginationTheme = 'primary' | 'white';
interface CarouselPreviousProps extends ComponentProps<typeof Button> {
  theme?: PaginationTheme;
}

const CarouselPrevious = forwardRef<HTMLButtonElement, CarouselPreviousProps>(
  ({ className, variant = 'ghost', size = 'icon', theme = 'primary', ...props }, ref) => {
    const { scrollPrev, canScrollPrev, scrollSnaps } = useCarousel();

    if (!scrollSnaps || scrollSnaps.length <= 1) return null;

    return (
      <Button
        ref={ref}
        variant={variant}
        size={size}
        className={cn(
          'h-10 w-10 rounded-full disabled:opacity-100',
          theme === 'primary' && 'text-primary-600 hover:text-primary-600 disabled:text-gray-300',
          theme === 'white' && 'hover:text-primary-600 text-white disabled:text-gray-300',
          className,
        )}
        disabled={!canScrollPrev}
        onClick={scrollPrev}
        {...props}
      >
        <div className="h-5 w-8">
          <svg
            width="100%"
            height="100%"
            viewBox="0 0 41 21"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M37.2527 10.5927H3.6416M3.6416 10.5927L10.4472 17.3982M3.6416 10.5927L10.4472 3.78711"
              stroke="currentColor"
              strokeWidth="1.66667"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>
        <span className="sr-only">Previous slide</span>
      </Button>
    );
  },
);
CarouselPrevious.displayName = 'CarouselPrevious';

interface CarouselNextProps extends ComponentProps<typeof Button> {
  theme?: PaginationTheme;
}

const CarouselNext = forwardRef<HTMLButtonElement, CarouselNextProps>(
  ({ className, variant = 'ghost', size = 'icon', theme = 'primary', ...props }, ref) => {
    const { scrollNext, canScrollNext, scrollSnaps } = useCarousel();

    if (!scrollSnaps || scrollSnaps.length <= 1) return null;

    return (
      <Button
        ref={ref}
        variant={variant}
        size={size}
        className={cn(
          'h-10 w-10 rounded-full disabled:opacity-100',
          theme === 'primary' && 'text-primary-600 hover:text-primary-600 disabled:text-gray-300',
          theme === 'white' && 'hover:text-primary-600 text-white disabled:text-gray-300',
          className,
        )}
        disabled={!canScrollNext}
        onClick={scrollNext}
        {...props}
      >
        <div className="h-5 w-8">
          <svg
            width="100%"
            height="100%"
            viewBox="0 0 41 21"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M3.64182 10.5927H37.2529M37.2529 10.5927L30.4474 17.3982M37.2529 10.5927L30.4474 3.78711"
              stroke="currentColor"
              strokeWidth="1.66667"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>
        <span className="sr-only">Next slide</span>
      </Button>
    );
  },
);
CarouselNext.displayName = 'CarouselNext';

interface CarouselDotsProps extends ComponentProps<'div'> {
  theme?: PaginationTheme;
  layoutID: string;
}

const CarouselDots = forwardRef<HTMLDivElement, CarouselDotsProps>(
  ({ className, theme = 'primary', layoutID, ...props }, ref) => {
    const { scrollSnaps, selectedIndex, scrollTo } = useCarousel();

    if (!scrollSnaps || scrollSnaps.length <= 1) return null;

    if (!isArrayWithLength(scrollSnaps)) {
      return <Skeleton className={cn('h-6 w-28', theme === 'white' && 'bg-white')} />;
    }

    return (
      <div ref={ref} className={cn('flex items-center justify-center', className)} {...props}>
        {scrollSnaps?.map((dot, index) => (
          <button
            key={dot}
            onClick={() => scrollTo(index)}
            className="flex size-6 items-center justify-center"
          >
            <div
              className={cn(
                'relative block h-2 w-2 rounded-full',
                theme === 'primary' && 'bg-gray-300',
                theme === 'white' && 'bg-white',
              )}
            >
              {index === selectedIndex && (
                <motion.div
                  layoutId={`carousel-dot-${layoutID}`}
                  className={cn(
                    'pointer-events-none absolute inset-0 z-10 rounded-full',
                    theme === 'primary' && 'bg-primary-600',
                    theme === 'white' && 'bg-naturaily-butterscotch',
                  )}
                />
              )}
            </div>

            <span className="sr-only">Next slide - {index}</span>
          </button>
        ))}
      </div>
    );
  },
);
CarouselDots.displayName = 'CarouselDots';

export { Carousel, CarouselContent, CarouselItem, CarouselPrevious, CarouselNext, CarouselDots };
export { CarouselGradient } from './CarouselGradient';
